<script setup lang="ts">
import { ref, onBeforeMount, computed, watch, toValue } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useMixins } from '@/views/internal/useMixins';
import { UserInfo } from '@/modules/user/UserInfo';
import { useUserInfo } from '@/modules/user/useUserInfo';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import Tab from 'primevue/tab';
import TabList from 'primevue/tablist';
import TabPanel from 'primevue/tabpanel';
import TabPanels from 'primevue/tabpanels';
import Tabs from 'primevue/tabs';
import ConfigurationRole from './ConfigurationRole.vue';
import GroupTravelHomeRole from './GroupTravelHomeRole.vue';
import MyQuoteRole from './MyQuoteRole.vue';
import QuoteManagementRole from './QuoteManagementRole.vue';
import ReportRole from './ReportRole.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';

const toast = useToast();
const { t } = useI18n({ useScope: 'global' });
const currentUser = ref<UserInfo>();
const loading = ref(false);
const group_travel_role = ref([]);
const my_quote_role = ref([]);
const advance_quote_role = ref([]);
const air_configuration = ref([]);
const gateway_configuration = ref([]);
const hotel_configuration = ref([]);
const destination_configuration = ref([]);
const flight_rule_configuration = ref([]);
const gate_duration_configuration = ref([]);
const tour_combination_configuration: any = ref([]);
const tour_detail_configuration: any = ref([]);
const quote_management_role = ref([]);
const report_role = ref([]);
const quote_status_role = ref([]);
const worksheet_package_role = ref([]);
const worksheet_package_additional = ref([]);
const worksheet_tour_quote_header: any = ref([]);
const worksheet_tour_flight_header: any = ref([]);
const worksheet_tour_hotel_header: any = ref([]);
const worksheet_tour_additional: any = ref([]);
const worksheet_tour_other_section: any = ref([]);
const worksheet_air_role = ref([]);
const worksheet_cruise_role = ref([]);
const worksheet_hotel_role = ref([]);
const configuration_role = ref([]);
const { convertObjectToArray, convertArrayToObject } = useMixins();
const userInfo = useUserInfo();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;

const listRole = computed(() => userAuthorizationsStore.listRolePermission);

const updateValue = (newVal: any) => {
  group_travel_role.value = convertObjectToArray(newVal.travel_home);
  my_quote_role.value = convertObjectToArray(newVal.my_quote);
  advance_quote_role.value = convertObjectToArray(newVal.advance_quote);
  quote_management_role.value = convertObjectToArray(newVal.quote_management);
  report_role.value = convertObjectToArray(newVal.report);
  quote_status_role.value = convertObjectToArray(newVal.quote_status);
  worksheet_package_role.value = convertObjectToArray(newVal.worksheet_package);
  worksheet_air_role.value = convertObjectToArray(newVal.worksheet_air_only);
  worksheet_cruise_role.value = convertObjectToArray(newVal.worksheet_cruise);
  worksheet_hotel_role.value = convertObjectToArray(newVal.worksheet_hotel);
  configuration_role.value = convertObjectToArray(newVal.configurationPage);
  worksheet_package_additional.value = convertObjectToArray(newVal.worksheet_package_additional);
  air_configuration.value = convertObjectToArray(newVal.air_configuration);
  gateway_configuration.value = convertObjectToArray(newVal.gateway_configuration);
  hotel_configuration.value = convertObjectToArray(newVal.hotel_configuration);
  destination_configuration.value = convertObjectToArray(newVal.destination_configuration);
  flight_rule_configuration.value = convertObjectToArray(newVal.flight_rule_configuration);
  gate_duration_configuration.value = convertObjectToArray(newVal.gate_duration_configuration);
  tour_combination_configuration.value = convertObjectToArray(newVal.tour_combination_configuration);
  tour_detail_configuration.value = convertObjectToArray(newVal.tour_detail_configuration);
  worksheet_tour_quote_header.value = convertObjectToArray(newVal.worksheet_tour_quote_header);
  worksheet_tour_flight_header.value = convertObjectToArray(newVal.worksheet_tour_flight_header);
  worksheet_tour_hotel_header.value = convertObjectToArray(newVal.worksheet_tour_hotel_header);
  worksheet_tour_additional.value = convertObjectToArray(newVal.worksheet_tour_additional);
  worksheet_tour_other_section.value = convertObjectToArray(newVal.worksheet_tour_other_section);
};

const handleSaveRole = async () => {
  try {
    const body = {
      travel_home: convertArrayToObject(group_travel_role.value),
      quote_management: convertArrayToObject(quote_management_role.value),
      advance_quote: convertArrayToObject(advance_quote_role.value),
      worksheet_air_only: convertArrayToObject(worksheet_air_role.value),
      worksheet_cruise: convertArrayToObject(worksheet_cruise_role.value),
      worksheet_hotel: convertArrayToObject(worksheet_hotel_role.value),
      worksheet_package: convertArrayToObject(worksheet_package_role.value),
      report: convertArrayToObject(report_role.value),
      configurationPage: convertArrayToObject(configuration_role.value),
      air_configuration: convertArrayToObject(air_configuration.value),
      gateway_configuration: convertArrayToObject(gateway_configuration.value),
      hotel_configuration: convertArrayToObject(hotel_configuration.value),
      destination_configuration: convertArrayToObject(destination_configuration.value),
      flight_rule_configuration: convertArrayToObject(flight_rule_configuration.value),
      gate_duration_configuration: convertArrayToObject(gate_duration_configuration.value),
      tour_combination_configuration: convertArrayToObject(tour_combination_configuration.value),
      tour_detail_configuration: convertArrayToObject(tour_detail_configuration.value),
      quote_status: convertArrayToObject(quote_status_role.value),
      my_quote: convertArrayToObject(my_quote_role.value),
      worksheet_package_additional: convertArrayToObject(worksheet_package_additional.value),
      worksheet_tour_quote_header: convertArrayToObject(worksheet_tour_quote_header.value),
      worksheet_tour_flight_header: convertArrayToObject(worksheet_tour_flight_header.value),
      worksheet_tour_hotel_header: convertArrayToObject(worksheet_tour_hotel_header.value),
      worksheet_tour_additional: convertArrayToObject(worksheet_tour_additional.value),
      worksheet_tour_other_section: convertArrayToObject(worksheet_tour_other_section.value)
    };
    await userAuthorizationsStore.updatePermission(body);
    userAuthorizationsStore.getListPermissionByRole(toValue(currentUser)?.role);
    toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
  } catch (error) {
    toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
  }
};

onBeforeMount(async () => {
  try {
    loading.value = true;
    currentUser.value = await userInfo.getUserInfo();
    await userAuthorizationsStore.getListRolePermision();
  } finally {
    loading.value = false;
  }
});

watch(
  listRole,
  (newVal: any) => {
    updateValue(newVal);
  },
  { immediate: true }
);
</script>
<template>
  <LoadingSpinner v-if="loading" />
  <div class="card role-management">
    <h3 class="h5style">{{ $t('role.role_management') }}</h3>
    <Tabs :value="0">
      <TabList>
        <Tab :key="0" :value="0">{{ $t('role.group_travel_home') }}</Tab>
        <Tab :key="1" :value="1">{{ $t('role.my_quote') }}</Tab>
        <Tab :key="2" :value="2">{{ $t('role.quote_management') }}</Tab>
        <Tab :key="3" :value="3">{{ $t('role.administration') }}</Tab>
        <Tab :key="4" :value="4">{{ $t('role.report') }}</Tab>
      </TabList>
      <TabPanels>
        <TabPanel :key="0" :value="0"><GroupTravelHomeRole v-model:groupTravelRole="advance_quote_role" :loading="loading"></GroupTravelHomeRole></TabPanel>
        <TabPanel :key="1" :value="1"><MyQuoteRole v-model:myQuoteRole="my_quote_role" :loading="loading"></MyQuoteRole></TabPanel>
        <TabPanel :key="2" :value="2">
          <QuoteManagementRole
            v-model:quoteManagementRole="quote_management_role"
            v-model:worksheetAirRole="worksheet_air_role"
            v-model:worksheetPackageRole="worksheet_package_role"
            v-model:worksheetHotelRole="worksheet_hotel_role"
            v-model:worksheetCruiseRole="worksheet_cruise_role"
            v-model:worksheetPackageAdditional="worksheet_package_additional"
            v-model:worksheetTourQuoteHeader="worksheet_tour_quote_header"
            v-model:worksheetTourFlightHeader="worksheet_tour_flight_header"
            v-model:worksheetTourHotelHeader="worksheet_tour_hotel_header"
            v-model:worksheetTourAdditional="worksheet_tour_additional"
            v-model:worksheetTourOtherSection="worksheet_tour_other_section"
            :loading="loading"
          >
          </QuoteManagementRole>
        </TabPanel>
        <TabPanel :key="3" :value="3">
          <ConfigurationRole
            v-model:configurationRole="configuration_role"
            :air_configuration="air_configuration"
            :hotel_configuration="hotel_configuration"
            :loading="loading"
            :gateway_configuration="gateway_configuration"
            :destination_configuration="destination_configuration"
            :flight_rule_configuration="flight_rule_configuration"
            :gate_duration_configuration="gate_duration_configuration"
            :tour_combination_configuration="tour_combination_configuration"
            :tour_detail_configuration="tour_detail_configuration"
          >
          </ConfigurationRole>
        </TabPanel>
        <TabPanel :key="4" :value="4"><ReportRole v-model:reportRole="report_role" :loading="loading"></ReportRole></TabPanel>
      </TabPanels>
    </Tabs>
    <div class="flex justify-content-end" v-if="authorizations?.configurationPage?.role_update_edit">
      <Button id="btn-save" :label="$t('role.save')" @click="handleSaveRole()" />
    </div>
  </div>
</template>
